<script setup lang="ts">
import { useVModel } from '@vueuse/core';
import $ from 'jquery';
import {
    computed, onMounted, type PropType, shallowRef, unref, watch,
} from 'vue';

import { useGenerateIds, useJqueryEventListener } from '#public_ts/composables';
import type { JqueryEventListener } from '#public_ts/composables/jquery-events';

const props = defineProps({
    name: { type: String as PropType<string | undefined>, required: false, default: undefined },
    id: { type: String as PropType<string | undefined>, required: false, default: undefined },
    on: { type: String as PropType<string | undefined>, required: false, default: undefined },
    off: { type: String as PropType<string | undefined>, required: false, default: undefined },
    modelValue: { type: [String, Number, null] as PropType<string | number | null | undefined>, required: false, default: undefined },
    booleanValue: { type: [Boolean, null] as PropType<boolean | null | undefined>, required: false, default: undefined },
});
//eslint-obsolete no-spaced-func
const emit = defineEmits<{
    (event: 'update:modelValue', value: string): void;
    (event: 'update:booleanValue', value: boolean): void;
}>();
// must be passive! otherwise modelValue won't work
const selectedRadio = useVModel(props, 'booleanValue', emit, { passive: true });
const _on = computed(() => String(props.on ?? 'on'));
const _off = computed(() => String(props.off ?? ''));
const buttonGroupElement = shallowRef<HTMLElement | undefined>();
const generateIds = useGenerateIds();
const controlId = props.id || generateIds();
const controlName = (props.name === '--') ? undefined : (props.name || controlId);

function updateValue(useStringValue?: boolean) {
    //synchronize initially and on the go
    //initial setup
    //if model value is provided - prefer it
    //if boolean value is not provided - default to false
    let stringValue = props.modelValue;
    if(((stringValue !== undefined) || (useStringValue === true)) && (useStringValue !== false)) {
        const value = (props.modelValue === unref(_on));
        if(value !== unref(selectedRadio)) {
            selectedRadio.value = value;
        }
    } else {
        //order is important, first we set modelValue, then booleanValue
        //in this case we can catch when modelValue === undefined and booleanValue === undefined
        stringValue = unref(selectedRadio) ? unref(_on) : unref(_off);
        if(stringValue !== props.modelValue) {
            emit('update:modelValue', stringValue);
        }
    }
}

watch(() => props.modelValue, () => {
    updateValue(true);
});

watch(selectedRadio, () => {
    updateValue(false);
});

onMounted(() => {
    updateValue();
});

//v-model doesn't work here, because user actually clicks on label, and bootstrap manages everything
//bootstrap sends if (changed) $input.trigger('change'); which is not handled by vue
//just 'change' don't work, sometimes bootstrap skips this event, don't know why
const onChange: JqueryEventListener = function(event) {
    let radio = $((event as JQuery.TriggeredEvent).target);
    if(!radio.is('input')) {
        radio = radio.find('input');
    }
    selectedRadio.value = radio.val() === unref(_on);
};
useJqueryEventListener(buttonGroupElement, ['change', 'click', 'auto-changed'], onChange);
</script>
<template>
    <div :id="controlId" ref="buttonGroupElement" class="btn-group" data-toggle="buttons">
        <label :id="`${controlId}-on`" class="btn btn-yes-no" :class="selectedRadio ? 'active' : ''">
            <input :checked="!!selectedRadio || undefined" type="radio" class="yesnocheckbox" :name="controlName" :value="_on">Sì
        </label>
        <label :id="`${controlId}-off`" class="btn btn-yes-no" :class="selectedRadio ? '' : 'active'">
            <input :checked="!selectedRadio || undefined" type="radio" class="yesnocheckbox" :name="controlName" :value="_off">No
        </label>
    </div>
</template>
